import type {
  Qualification,
  QualificationStats,
  SearchQualificationsResponse,
  AuditHistoryEntry,
} from "../types";

// Helper to generate realistic qualifications
function generateMockQualifications(count: number): Qualification[] {
  const qualifications: Qualification[] = [];
  const now = Date.now();

  const statuses: Array<"pending" | "accepted" | "rejected"> = [
    "accepted",
    "pending",
    "rejected",
  ];

  for (let i = 0; i < count; i++) {
    const timestamp = now - i * 86400000 - Math.random() * 86400000; // Spread over days
    const status = statuses[Math.floor(Math.random() * statuses.length)];

    // Generate score based on status (accepted tend to be higher)
    let score: number;
    if (status === "accepted") {
      score = 0.7 + Math.random() * 0.3; // 70-100
    } else if (status === "pending") {
      score = 0.5 + Math.random() * 0.3; // 50-80
    } else {
      score = 0.1 + Math.random() * 0.5; // 10-60
    }

    const isReviewed = status !== "pending";
    const completed = Math.random() > 0.1; // 90% completed

    qualifications.push({
      id: `qualification_${i + 1}`, // Stable ID without timestamp
      client_id: "demo_client",
      customer_id: `customer_${Math.floor(Math.random() * 50) + 1}`,
      form_id: `form_${(i % 3) + 1}`, // Rotate between 3 forms
      status,
      responses: {
        experience: i % 2 === 0 ? "yes" : "no",
        years_experience: Math.floor(Math.random() * 20) + 1,
        role: ["Developer", "Designer", "Manager", "Analyst"][i % 4],
        motivation:
          "I am very interested in this opportunity and believe my skills align well with your requirements.",
        availability: ["Immediate", "2 weeks", "1 month"][i % 3],
      },
      score: Math.round(score * 10) / 10, // Round to 1 decimal
      original_score: Math.round((score - 5 + Math.random() * 10) * 10) / 10,
      form_snapshot: {
        questions: [
          {
            id: "q1",
            type: "boolean",
            label: "Do you have relevant experience?",
            required: true,
            scoring_mode: "weight",
            scoring: {
              values: { yes: 20, no: 0 },
              default: 0,
              weight: 1.5,
            },
          },
          {
            id: "q2",
            type: "number",
            label: "Years of experience",
            required: true,
            min: 0,
            max: 50,
            scoring_mode: "weight",
            scoring: {
              ranges: [
                { operator: "gte", threshold: 5, value: 20 },
                { operator: "gte", threshold: 2, value: 15 },
                { operator: "gte", threshold: 0, value: 10 },
              ],
              default: 0,
              weight: 1.2,
            },
          },
          {
            id: "q3",
            type: "select",
            label: "What is your primary role?",
            required: true,
            options: ["Developer", "Designer", "Manager", "Analyst"],
            scoring_mode: "weight",
            scoring: {
              values: {
                Developer: 15,
                Designer: 12,
                Manager: 10,
                Analyst: 10,
              },
              default: 5,
              weight: 1.0,
            },
          },
          {
            id: "q4",
            type: "text",
            label: "Why are you interested in this position?",
            required: true,
            scoring_mode: "weight",
            scoring: {
              default: 10,
              weight: 0.8,
              scoring_range: { min: 0, max: 25 },
              scoring_logic: "Rate based on clarity, enthusiasm, and relevance",
            },
          },
        ],
      },
      adapter: {
        adapter: "manual",
        id: `adapter_${i}`,
      },
      completed,
      submitted_at: timestamp,
      reviewed_at: isReviewed ? timestamp + 3600000 : undefined,
      reviewed_by: isReviewed ? "admin_user" : undefined,
      created_at: timestamp,
      updated_at: timestamp + (isReviewed ? 3600000 : 0),
      created_by: "system",
      updated_by: isReviewed ? "admin_user" : "system",
      is_deleted: false,
    });
  }

  return qualifications;
}

// Generate a stable pool of qualifications once
const MOCK_QUALIFICATIONS_POOL = generateMockQualifications(50);

// Mock data for useQualifications
export const mockQualificationsResult: SearchQualificationsResponse = {
  data: MOCK_QUALIFICATIONS_POOL,
  total: 156,
  limit: 50,
  offset: 0,
  has_more: true,
};

// Mock data for useQualificationStats
export const mockQualificationStats: QualificationStats = {
  total: 156,
  by_status: {
    accepted: 89,
    pending: 42,
    rejected: 25,
  },
  average_score: 68.5,
};

// Mock data for single qualification
export function getMockQualification(qualificationId: string): Qualification {
  const qualification = MOCK_QUALIFICATIONS_POOL.find(
    (q) => q.id === qualificationId,
  );
  return qualification || MOCK_QUALIFICATIONS_POOL[0];
}

// Mock data for qualification history
export function getMockQualificationHistory(
  qualificationId: string,
): AuditHistoryEntry[] {
  const now = Date.now();
  const baseQualification = getMockQualification(qualificationId);

  // Newest first, as the server sends it.
  return [
    {
      status: baseQualification.status,
      score: baseQualification.score,
      completed: true,
      operation: "UPDATE",
      operation_timestamp: now - 1800000, // 30 minutes ago
      changes: ["status"],
      actor: {
        kind: "user",
        id: "admin_user",
        name: "Camille Martin",
        avatar: null,
      },
    },
    {
      status: "pending",
      score: baseQualification.score,
      completed: true,
      operation: "UPDATE",
      operation_timestamp: now - 3600000, // 1 hour ago
      changes: ["score", "completed"],
      actor: { kind: "system" },
    },
    {
      status: "pending",
      score: baseQualification.original_score || baseQualification.score,
      completed: false,
      operation: "INSERT",
      operation_timestamp: now - 7200000, // 2 hours ago
      changes: [],
      actor: { kind: "system" },
    },
  ];
}
